import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SaveIcon, ArrowLeftIcon, HelpCircleIcon, MapPinIcon, CheckIcon, XIcon, UploadIcon, AlertCircleIcon, RefreshCwIcon, HistoryIcon, BuildingIcon, ArrowRightIcon, PlusIcon, TrashIcon, CalendarIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock parent merchants data for dropdown
const parentMerchantsData = [{
  id: 'PM001',
  name: 'Global Retail Group'
}, {
  id: 'PM002',
  name: 'Fast Food Alliance'
}, {
  id: 'PM003',
  name: 'Tech Distribution Network'
}, {
  id: 'PM004',
  name: 'Hospitality Group International'
}, {
  id: 'PM005',
  name: 'Urban Transport Consortium'
}];
// Payment modes for limit management
const paymentModes = ['POS', 'SoftPos', 'Online', 'Mobile POS', 'QR Payments', 'RTP Payments'];
// Frequency options for limit management
const frequencyOptions = ['Daily', 'Monthly', 'Yearly', 'Per Transaction'];
const MerchantForm: React.FC = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [activeTab, setActiveTab] = useState('merchant-kyc');
  const [formStatus, setFormStatus] = useState<'draft' | 'submitted' | 'approved' | 'rejected'>('draft');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [statusChangeReason, setStatusChangeReason] = useState('');
  // Mock status history for editing
  const statusHistory = isEditing ? [{
    previousStatus: 'pending',
    newStatus: 'active',
    changedBy: 'admin@example.com',
    changedAt: '2023-05-20T10:30:00Z',
    reason: 'All requirements met'
  }] : [];
  // Mock merchant data for editing
  const merchantData = isEditing ? {
    name: 'ABC Corporation',
    businessType: 'Retail',
    cnic: '12345-6789012-3',
    ntn: 'NTN-12345',
    registrationNumber: 'REG-98765',
    verificationStatus: 'verified',
    businessName: 'ABC Corporation',
    industryType: 'Retail',
    mcc: '5411',
    address: '123 Main St, Anytown',
    contactPerson: 'John Smith',
    contactEmail: 'john@abccorp.com',
    contactPhone: '+1 (555) 123-4567',
    parentMerchantId: '',
    settlementType: 'individual',
    bankAccount: '1234567890',
    bankName: 'First National Bank',
    branchCode: 'FNB-001',
    settlementCurrency: 'USD',
    settlementFrequency: 'daily',
    dailyLimit: 10000,
    monthlyLimit: 200000,
    rtpLimit: 5000,
    refundLimit: 2000,
    allowedPaymentMethods: ['pos', 'qr', 'online'],
    allowedCurrencies: ['USD', 'EUR'],
    documents: {
      cnic: {
        name: 'cnic.pdf',
        uploaded: true
      },
      proofOfAddress: {
        name: 'address.pdf',
        uploaded: true
      },
      businessRegistration: {
        name: 'registration.pdf',
        uploaded: true
      },
      bankLetter: {
        name: 'bank_letter.pdf',
        uploaded: true
      }
    },
    complianceStatus: 'passed',
    complianceNotes: 'All checks passed',
    location: {
      lat: 40.7128,
      lng: -74.006
    },
    status: 'active',
    statusHistory: statusHistory,
    lastActivityDate: '2023-10-25',
    limits: [{
      id: '1',
      paymentMode: 'POS',
      amount: 5000,
      frequency: 'Daily',
      effectiveDate: '2023-11-01',
      endDate: '2023-12-31'
    }, {
      id: '2',
      paymentMode: 'QR Payments',
      amount: 50000,
      frequency: 'Monthly',
      effectiveDate: '2023-11-01',
      endDate: null
    }]
  } : {
    name: '',
    businessType: '',
    cnic: '',
    ntn: '',
    registrationNumber: '',
    verificationStatus: 'pending',
    businessName: '',
    industryType: '',
    mcc: '',
    address: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    parentMerchantId: '',
    settlementType: 'individual',
    bankAccount: '',
    bankName: '',
    branchCode: '',
    settlementCurrency: 'USD',
    settlementFrequency: 'daily',
    dailyLimit: 5000,
    monthlyLimit: 100000,
    rtpLimit: 1000,
    refundLimit: 1000,
    allowedPaymentMethods: ['pos'],
    allowedCurrencies: ['USD'],
    documents: {
      cnic: {
        name: '',
        uploaded: false
      },
      proofOfAddress: {
        name: '',
        uploaded: false
      },
      businessRegistration: {
        name: '',
        uploaded: false
      },
      bankLetter: {
        name: '',
        uploaded: false
      }
    },
    complianceStatus: 'pending',
    complianceNotes: '',
    location: {
      lat: 0,
      lng: 0
    },
    status: 'pending',
    statusHistory: [],
    lastActivityDate: new Date().toISOString().split('T')[0],
    limits: []
  };
  const [merchant, setMerchant] = useState(merchantData);
  const [newLimit, setNewLimit] = useState({
    paymentMode: '',
    amount: 0,
    frequency: '',
    effectiveDate: '',
    endDate: ''
  });
  const [limitError, setLimitError] = useState('');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setMerchant(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleNewLimitChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setNewLimit(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const validateLimitDates = () => {
    // Check if dates are valid
    if (!newLimit.effectiveDate) {
      setLimitError('Effective date is required');
      return false;
    }
    const effectiveDate = new Date(newLimit.effectiveDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (effectiveDate < today) {
      setLimitError('Effective date must be current or future date');
      return false;
    }
    if (newLimit.endDate) {
      const endDate = new Date(newLimit.endDate);
      if (endDate <= effectiveDate) {
        setLimitError('End date must be greater than effective date');
        return false;
      }
    }
    // Check for overlapping dates with same payment mode and frequency
    const overlapping = merchant.limits.some(limit => limit.paymentMode === newLimit.paymentMode && limit.frequency === newLimit.frequency && (newLimit.endDate && limit.endDate ? new Date(newLimit.effectiveDate) <= new Date(limit.endDate) && new Date(newLimit.endDate) >= new Date(limit.effectiveDate) : newLimit.endDate ? new Date(limit.effectiveDate) <= new Date(newLimit.endDate) : limit.endDate ? new Date(newLimit.effectiveDate) <= new Date(limit.endDate) : true));
    if (overlapping) {
      setLimitError('Date ranges overlap with an existing limit for the same payment mode and frequency');
      return false;
    }
    return true;
  };
  const addNewLimit = () => {
    if (!newLimit.paymentMode || !newLimit.frequency || !newLimit.amount || !newLimit.effectiveDate) {
      setLimitError('Please fill all required fields');
      return;
    }
    if (!validateLimitDates()) {
      return;
    }
    const limitToAdd = {
      id: Date.now().toString(),
      ...newLimit
    };
    setMerchant(prev => ({
      ...prev,
      limits: [...prev.limits, limitToAdd]
    }));
    // Reset form
    setNewLimit({
      paymentMode: '',
      amount: 0,
      frequency: '',
      effectiveDate: '',
      endDate: ''
    });
    setLimitError('');
  };
  const removeLimit = (id: string) => {
    setMerchant(prev => ({
      ...prev,
      limits: prev.limits.filter(limit => limit.id !== id)
    }));
  };
  const handleStatusChange = () => {
    setNewStatus(merchant.status);
    setShowStatusModal(true);
  };
  const confirmStatusChange = () => {
    if (!newStatus || newStatus === merchant.status) {
      setShowStatusModal(false);
      return;
    }
    // Update merchant status and add to history
    const statusHistoryItem = {
      previousStatus: merchant.status,
      newStatus: newStatus,
      changedBy: 'current.user@example.com',
      changedAt: new Date().toISOString(),
      reason: statusChangeReason
    };
    setMerchant(prev => ({
      ...prev,
      status: newStatus,
      statusHistory: [statusHistoryItem, ...prev.statusHistory]
    }));
    setShowStatusModal(false);
    setStatusChangeReason('');
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    const methodName = name.replace('payment-', '');
    setMerchant(prev => ({
      ...prev,
      allowedPaymentMethods: checked ? [...prev.allowedPaymentMethods, methodName] : prev.allowedPaymentMethods.filter(m => m !== methodName)
    }));
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    const currency = name.replace('currency-', '');
    setMerchant(prev => ({
      ...prev,
      allowedCurrencies: checked ? [...prev.allowedCurrencies, currency] : prev.allowedCurrencies.filter(c => c !== currency)
    }));
  };
  const handleFileUpload = (documentType: keyof typeof merchant.documents) => {
    // Mock file upload
    setMerchant(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [documentType]: {
          name: `${documentType}_${Date.now()}.pdf`,
          uploaded: true
        }
      }
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Submit logic would go here
    setFormStatus('submitted');
    // Navigate back to merchant list
    setTimeout(() => {
      navigate('/merchants');
    }, 1500);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-4218" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-4219" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-4220" />;
      case 'suspended':
        return <StatusBadge status="info" text="Suspended" data-id="element-4221" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-4222" />;
    }
  };
  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };
  const tabs = [{
    id: 'merchant-kyc',
    label: 'Merchant KYC'
  }, {
    id: 'business-details',
    label: 'Business Details'
  }, {
    id: 'account-details',
    label: 'Account Details'
  }, {
    id: 'limit-management',
    label: 'Limit Management'
  }, {
    id: 'payment-methods',
    label: 'Payment Methods'
  }, {
    id: 'currency-setup',
    label: 'Currency Setup'
  }, {
    id: 'document-uploads',
    label: 'Document Uploads'
  }, {
    id: 'compliance-screening',
    label: 'Compliance Screening'
  }, {
    id: 'location-tagging',
    label: 'Location Tagging'
  }];
  return <div className="space-y-6" data-id="element-4223">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-4224">
        <div data-id="element-4225">
          <button onClick={() => navigate('/merchants')} className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-4226">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-4227" />
            Back to Merchants
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1" data-id="element-4228">
            {isEditing ? 'Edit Merchant' : 'New Merchant'}
          </h1>
        </div>
        <div className="mt-3 sm:mt-0 flex items-center space-x-3" data-id="element-4229">
          {formStatus === 'submitted' && <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-4230">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-4231" />
              Submitted Successfully
            </div>}
          {formStatus === 'rejected' && <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-4232">
              <XIcon className="h-4 w-4 mr-1" data-id="element-4233" />
              Rejected
            </div>}
          {isEditing && <div className="flex items-center space-x-2" data-id="element-4234">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4235">
                Status:
              </span>
              {getStatusBadge(merchant.status)}
            </div>}
          <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/merchants')} data-id="element-4236">
            Cancel
          </button>
          {isEditing && <button type="button" className="inline-flex items-center px-4 py-2 border border-purple-300 dark:border-purple-600 rounded-md shadow-sm text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30" onClick={handleStatusChange} data-id="element-4237">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-4238" />
              Change Status
            </button>}
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-4239">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-4240" />
            {isEditing ? 'Update' : 'Submit'} Merchant
          </button>
        </div>
      </div>
      {/* Form Status */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-4241">
        <div className="flex items-center justify-between" data-id="element-4242">
          <div className="flex items-center" data-id="element-4243">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2" data-id="element-4244">
              Form Status:
            </span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${formStatus === 'draft' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' : formStatus === 'submitted' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : formStatus === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`} data-id="element-4245">
              {formStatus.charAt(0).toUpperCase() + formStatus.slice(1)}
            </span>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4246">
            {isEditing ? 'Last updated:' : 'Created:'}{' '}
            {new Date().toLocaleDateString()}
          </div>
        </div>
        {isEditing && merchant.statusHistory.length > 0 && <div className="mt-3 flex justify-between items-center" data-id="element-4247">
            <div className="flex items-center" data-id="element-4248">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2" data-id="element-4249">
                Merchant Status:
              </span>
              {getStatusBadge(merchant.status)}
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-4250">
                Last changed:{' '}
                {formatDateTime(merchant.statusHistory[0].changedAt)}
              </span>
            </div>
            <button type="button" className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" onClick={() => setShowStatusHistoryModal(true)} data-id="element-4251">
              <HistoryIcon className="h-4 w-4 mr-1" data-id="element-4252" />
              View Status History
            </button>
          </div>}
        {formStatus === 'rejected' && <div className="mt-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded p-3 flex items-start" data-id="element-4253">
            <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5" data-id="element-4254" />
            <div data-id="element-4255">
              <p className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-4256">
                Application Rejected
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1" data-id="element-4257">
                Missing required documentation. Please upload proof of business
                registration and resubmit.
              </p>
            </div>
          </div>}
      </div>
      {/* Form Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-4258">
        <div className="overflow-x-auto" data-id="element-4259">
          <nav className="-mb-px flex space-x-8" data-id="element-4260">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}`} data-id="element-4261">
                {tab.label}
              </button>)}
          </nav>
        </div>
      </div>
      {/* Form Content */}
      <form onSubmit={handleSubmit} data-id="element-4262">
        {/* Merchant KYC */}
        {activeTab === 'merchant-kyc' && <Card data-id="element-4263">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4264">
              <div data-id="element-4265">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4266">
                  CNIC Number <span className="text-red-500" data-id="element-4267">*</span>
                </label>
                <div className="relative" data-id="element-4268">
                  <input type="text" name="cnic" value={merchant.cnic} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="00000-0000000-0" required data-id="element-4269" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-4270">
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 cursor-help" title="National ID Card Number" data-id="element-4271" />
                  </div>
                </div>
              </div>
              <div data-id="element-4272">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4273">
                  NTN
                </label>
                <input type="text" name="ntn" value={merchant.ntn} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="National Tax Number" data-id="element-4274" />
              </div>
              <div data-id="element-4275">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4276">
                  Business Type <span className="text-red-500" data-id="element-4277">*</span>
                </label>
                <select name="businessType" value={merchant.businessType} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-4278">
                  <option value="" data-id="element-4279">Select Business Type</option>
                  <option value="Sole Proprietorship" data-id="element-4280">
                    Sole Proprietorship
                  </option>
                  <option value="Partnership" data-id="element-4281">Partnership</option>
                  <option value="Corporation" data-id="element-4282">Corporation</option>
                  <option value="LLC" data-id="element-4283">Limited Liability Company (LLC)</option>
                  <option value="Non-Profit" data-id="element-4284">Non-Profit Organization</option>
                </select>
              </div>
              <div data-id="element-4285">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4286">
                  Business Registration Number{' '}
                  <span className="text-red-500" data-id="element-4287">*</span>
                </label>
                <input type="text" name="registrationNumber" value={merchant.registrationNumber} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Business Registration Number" required data-id="element-4288" />
              </div>
              <div data-id="element-4289">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4290">
                  Parent Merchant
                </label>
                <select name="parentMerchantId" value={merchant.parentMerchantId} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4291">
                  <option value="" data-id="element-4292">Select Parent Merchant (Optional)</option>
                  {parentMerchantsData.map(parent => <option key={parent.id} value={parent.id} data-id="element-4293">
                      {parent.name}
                    </option>)}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4294">
                  If selected, this merchant will be added as a child merchant
                </p>
              </div>
              <div data-id="element-4295">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4296">
                  Verification Status
                </label>
                <div className="flex items-center space-x-4 mt-2" data-id="element-4297">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${merchant.verificationStatus === 'verified' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : merchant.verificationStatus === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`} data-id="element-4298">
                    {merchant.verificationStatus.charAt(0).toUpperCase() + merchant.verificationStatus.slice(1)}
                  </div>
                  {merchant.verificationStatus === 'verified' && <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4299">
                      Verified on {new Date().toLocaleDateString()}
                    </span>}
                </div>
              </div>
              <div className="md:col-span-2" data-id="element-4300">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4301">
                  Beneficial Owners
                </label>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-4302">
                  <div className="flex justify-between items-center" data-id="element-4303">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4304">
                      Add individuals who own 25% or more of the business
                    </p>
                    <button type="button" className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50" data-id="element-4305">
                      Add Owner
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Card>}
        {/* Business Details */}
        {activeTab === 'business-details' && <Card data-id="element-4306">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4307">
              <div data-id="element-4308">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4309">
                  Business Name <span className="text-red-500" data-id="element-4310">*</span>
                </label>
                <input type="text" name="businessName" value={merchant.businessName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Legal Business Name" required data-id="element-4311" />
              </div>
              <div data-id="element-4312">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4313">
                  Industry Type <span className="text-red-500" data-id="element-4314">*</span>
                </label>
                <select name="industryType" value={merchant.industryType} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-4315">
                  <option value="" data-id="element-4316">Select Industry</option>
                  <option value="Retail" data-id="element-4317">Retail</option>
                  <option value="E-commerce" data-id="element-4318">E-commerce</option>
                  <option value="Food & Beverage" data-id="element-4319">Food & Beverage</option>
                  <option value="Healthcare" data-id="element-4320">Healthcare</option>
                  <option value="Technology" data-id="element-4321">Technology</option>
                  <option value="Travel" data-id="element-4322">Travel & Hospitality</option>
                  <option value="Education" data-id="element-4323">Education</option>
                  <option value="Financial" data-id="element-4324">Financial Services</option>
                  <option value="Other" data-id="element-4325">Other</option>
                </select>
              </div>
              <div data-id="element-4326">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4327">
                  MCC (Merchant Category Code){' '}
                  <span className="text-red-500" data-id="element-4328">*</span>
                </label>
                <div className="relative" data-id="element-4329">
                  <input type="text" name="mcc" value={merchant.mcc} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="e.g. 5411" required data-id="element-4330" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-4331">
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 cursor-help" title="4-digit code that classifies the business type" data-id="element-4332" />
                  </div>
                </div>
              </div>
              <div className="md:col-span-2" data-id="element-4333">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4334">
                  Business Address <span className="text-red-500" data-id="element-4335">*</span>
                </label>
                <textarea name="address" value={merchant.address} onChange={handleInputChange} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Full business address" required data-id="element-4336" />
              </div>
              <div data-id="element-4337">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4338">
                  Contact Person <span className="text-red-500" data-id="element-4339">*</span>
                </label>
                <input type="text" name="contactPerson" value={merchant.contactPerson} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Full name of primary contact" required data-id="element-4340" />
              </div>
              <div data-id="element-4341">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4342">
                  Contact Email <span className="text-red-500" data-id="element-4343">*</span>
                </label>
                <input type="email" name="contactEmail" value={merchant.contactEmail} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="email@example.com" required data-id="element-4344" />
              </div>
              <div data-id="element-4345">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4346">
                  Contact Phone <span className="text-red-500" data-id="element-4347">*</span>
                </label>
                <input type="tel" name="contactPhone" value={merchant.contactPhone} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="+1 (123) 456-7890" required data-id="element-4348" />
              </div>
            </div>
          </Card>}
        {/* Account Details */}
        {activeTab === 'account-details' && <Card data-id="element-4349">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4350">
              <div className="md:col-span-2" data-id="element-4351">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4352">
                  Settlement Type <span className="text-red-500" data-id="element-4353">*</span>
                </label>
                <select name="settlementType" value={merchant.settlementType} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-4354">
                  <option value="individual" data-id="element-4355">Individual Pay</option>
                  <option value="central" data-id="element-4356">Central Pay</option>
                </select>
                {merchant.settlementType === 'central' && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md" data-id="element-4357">
                    <span className="font-medium" data-id="element-4358">Central Pay Selected:</span>{' '}
                    Settlement will be done in the control account. No
                    additional account details are required.
                  </p>}
              </div>
              {merchant.settlementType === 'individual' && <>
                  <div data-id="element-4359">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4360">
                      Bank Account Number{' '}
                      <span className="text-red-500" data-id="element-4361">*</span>
                    </label>
                    <input type="text" name="bankAccount" value={merchant.bankAccount} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Bank Account Number" required data-id="element-4362" />
                  </div>
                  <div data-id="element-4363">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4364">
                      Bank Name <span className="text-red-500" data-id="element-4365">*</span>
                    </label>
                    <input type="text" name="bankName" value={merchant.bankName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Bank Name" required data-id="element-4366" />
                  </div>
                  <div data-id="element-4367">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4368">
                      Branch Code
                    </label>
                    <input type="text" name="branchCode" value={merchant.branchCode} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Branch Code" data-id="element-4369" />
                  </div>
                  <div data-id="element-4370">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4371">
                      Settlement Currency{' '}
                      <span className="text-red-500" data-id="element-4372">*</span>
                    </label>
                    <select name="settlementCurrency" value={merchant.settlementCurrency} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-4373">
                      <option value="USD" data-id="element-4374">USD - US Dollar</option>
                      <option value="EUR" data-id="element-4375">EUR - Euro</option>
                      <option value="GBP" data-id="element-4376">GBP - British Pound</option>
                      <option value="JPY" data-id="element-4377">JPY - Japanese Yen</option>
                      <option value="CAD" data-id="element-4378">CAD - Canadian Dollar</option>
                      <option value="AUD" data-id="element-4379">AUD - Australian Dollar</option>
                    </select>
                  </div>
                  <div data-id="element-4380">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4381">
                      Settlement Frequency{' '}
                      <span className="text-red-500" data-id="element-4382">*</span>
                    </label>
                    <select name="settlementFrequency" value={merchant.settlementFrequency} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-4383">
                      <option value="daily" data-id="element-4384">Daily</option>
                      <option value="weekly" data-id="element-4385">Weekly</option>
                      <option value="biweekly" data-id="element-4386">Bi-weekly</option>
                      <option value="monthly" data-id="element-4387">Monthly</option>
                    </select>
                  </div>
                </>}
            </div>
          </Card>}
        {/* Limit Management */}
        {activeTab === 'limit-management' && <Card data-id="element-4388">
            <div className="space-y-6" data-id="element-4389">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4390">
                <div data-id="element-4391">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4392">
                    Payment Mode <span className="text-red-500" data-id="element-4393">*</span>
                  </label>
                  <select name="paymentMode" value={newLimit.paymentMode} onChange={handleNewLimitChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4394">
                    <option value="" data-id="element-4395">Select Payment Mode</option>
                    {paymentModes.map(mode => <option key={mode} value={mode} data-id="element-4396">
                        {mode}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-4397">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4398">
                    Amount <span className="text-red-500" data-id="element-4399">*</span>
                  </label>
                  <div className="relative mt-1 rounded-md shadow-sm" data-id="element-4400">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-4401">
                      <span className="text-gray-500 dark:text-gray-400 sm:text-sm" data-id="element-4402">
                        $
                      </span>
                    </div>
                    <input type="number" name="amount" value={newLimit.amount || ''} onChange={handleNewLimitChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-7 pr-3 text-sm" placeholder="0.00" data-id="element-4403" />
                  </div>
                </div>
                <div data-id="element-4404">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4405">
                    Frequency <span className="text-red-500" data-id="element-4406">*</span>
                  </label>
                  <select name="frequency" value={newLimit.frequency} onChange={handleNewLimitChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4407">
                    <option value="" data-id="element-4408">Select Frequency</option>
                    {frequencyOptions.map(freq => <option key={freq} value={freq} data-id="element-4409">
                        {freq}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-4410">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4411">
                    Effective Date <span className="text-red-500" data-id="element-4412">*</span>
                  </label>
                  <div className="relative" data-id="element-4413">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-4414">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-4415" />
                    </div>
                    <input type="date" name="effectiveDate" value={newLimit.effectiveDate} onChange={handleNewLimitChange} min={new Date().toISOString().split('T')[0]} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-10 pr-3 text-sm" data-id="element-4416" />
                  </div>
                </div>
                <div data-id="element-4417">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4418">
                    End Date
                  </label>
                  <div className="relative" data-id="element-4419">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-4420">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-4421" />
                    </div>
                    <input type="date" name="endDate" value={newLimit.endDate} onChange={handleNewLimitChange} min={newLimit.effectiveDate || new Date().toISOString().split('T')[0]} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-10 pr-3 text-sm" data-id="element-4422" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4423">
                    Leave empty for no end date
                  </p>
                </div>
                <div className="flex items-end" data-id="element-4424">
                  <button type="button" onClick={addNewLimit} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4425">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-4426" />
                    Add Limit
                  </button>
                </div>
              </div>
              {limitError && <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-400" data-id="element-4427">
                  {limitError}
                </div>}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-4428">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4429">
                  Configured Limits
                </h3>
                {merchant.limits.length > 0 ? <div className="overflow-x-auto" data-id="element-4430">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4431">
                      <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-4432">
                        <tr data-id="element-4433">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4434">
                            Payment Mode
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4435">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4436">
                            Frequency
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4437">
                            Effective Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4438">
                            End Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4439">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4440">
                        {merchant.limits.map(limit => <tr key={limit.id} data-id="element-4441">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4442">
                              {limit.paymentMode}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4443">
                              ${parseFloat(limit.amount.toString()).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4444">
                              {limit.frequency}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4445">
                              {new Date(limit.effectiveDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4446">
                              {limit.endDate ? new Date(limit.endDate).toLocaleDateString() : 'No end date'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4447">
                              <button type="button" onClick={() => removeLimit(limit.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300" data-id="element-4448">
                                <TrashIcon className="h-5 w-5" data-id="element-4449" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div> : <div className="text-center py-4 text-gray-500 dark:text-gray-400" data-id="element-4450">
                    No limits configured yet. Add a limit using the form above.
                  </div>}
              </div>
              <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-4451">
                <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-center" data-id="element-4452">
                  <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-4453" />
                  Setting appropriate limits helps prevent fraud and manage
                  risk.
                </p>
              </div>
            </div>
          </Card>}
        {/* Payment Methods */}
        {activeTab === 'payment-methods' && <Card data-id="element-4454">
            <div className="space-y-4" data-id="element-4455">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4456">
                Select the payment methods this merchant is allowed to use:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4457">
                <div className="flex items-center" data-id="element-4458">
                  <input type="checkbox" id="payment-pos" name="payment-pos" checked={merchant.allowedPaymentMethods.includes('pos')} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4459" />
                  <label htmlFor="payment-pos" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4460">
                    POS (Point of Sale)
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4461">
                  <input type="checkbox" id="payment-qr" name="payment-qr" checked={merchant.allowedPaymentMethods.includes('qr')} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4462" />
                  <label htmlFor="payment-qr" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4463">
                    QR Code Payments
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4464">
                  <input type="checkbox" id="payment-online" name="payment-online" checked={merchant.allowedPaymentMethods.includes('online')} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4465" />
                  <label htmlFor="payment-online" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4466">
                    Online Payments
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4467">
                  <input type="checkbox" id="payment-rtp" name="payment-rtp" checked={merchant.allowedPaymentMethods.includes('rtp')} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4468" />
                  <label htmlFor="payment-rtp" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4469">
                    Request to Pay (RTP)
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4470">
                  <input type="checkbox" id="payment-nfc" name="payment-nfc" checked={merchant.allowedPaymentMethods.includes('nfc')} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4471" />
                  <label htmlFor="payment-nfc" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4472">
                    NFC / Contactless
                  </label>
                </div>
              </div>
              <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-4473">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4474">
                  Allowed Card Types
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4475">
                  <div className="flex items-center" data-id="element-4476">
                    <input type="checkbox" id="card-visa" name="card-visa" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" defaultChecked data-id="element-4477" />
                    <label htmlFor="card-visa" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4478">
                      Visa
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-4479">
                    <input type="checkbox" id="card-mastercard" name="card-mastercard" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" defaultChecked data-id="element-4480" />
                    <label htmlFor="card-mastercard" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4481">
                      Mastercard
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-4482">
                    <input type="checkbox" id="card-amex" name="card-amex" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" defaultChecked data-id="element-4483" />
                    <label htmlFor="card-amex" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4484">
                      American Express
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-4485">
                    <input type="checkbox" id="card-discover" name="card-discover" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4486" />
                    <label htmlFor="card-discover" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4487">
                      Discover
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-4488">
                    <input type="checkbox" id="card-jcb" name="card-jcb" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4489" />
                    <label htmlFor="card-jcb" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4490">
                      JCB
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-4491">
                    <input type="checkbox" id="card-unionpay" name="card-unionpay" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4492" />
                    <label htmlFor="card-unionpay" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4493">
                      UnionPay
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </Card>}
        {/* Currency Setup */}
        {activeTab === 'currency-setup' && <Card data-id="element-4494">
            <div className="space-y-4" data-id="element-4495">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4496">
                Select the currencies this merchant is allowed to accept:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4497">
                <div className="flex items-center" data-id="element-4498">
                  <input type="checkbox" id="currency-USD" name="currency-USD" checked={merchant.allowedCurrencies.includes('USD')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4499" />
                  <label htmlFor="currency-USD" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4500">
                    USD - US Dollar
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4501">
                  <input type="checkbox" id="currency-EUR" name="currency-EUR" checked={merchant.allowedCurrencies.includes('EUR')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4502" />
                  <label htmlFor="currency-EUR" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4503">
                    EUR - Euro
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4504">
                  <input type="checkbox" id="currency-GBP" name="currency-GBP" checked={merchant.allowedCurrencies.includes('GBP')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4505" />
                  <label htmlFor="currency-GBP" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4506">
                    GBP - British Pound
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4507">
                  <input type="checkbox" id="currency-JPY" name="currency-JPY" checked={merchant.allowedCurrencies.includes('JPY')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4508" />
                  <label htmlFor="currency-JPY" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4509">
                    JPY - Japanese Yen
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4510">
                  <input type="checkbox" id="currency-CAD" name="currency-CAD" checked={merchant.allowedCurrencies.includes('CAD')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4511" />
                  <label htmlFor="currency-CAD" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4512">
                    CAD - Canadian Dollar
                  </label>
                </div>
                <div className="flex items-center" data-id="element-4513">
                  <input type="checkbox" id="currency-AUD" name="currency-AUD" checked={merchant.allowedCurrencies.includes('AUD')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4514" />
                  <label htmlFor="currency-AUD" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4515">
                    AUD - Australian Dollar
                  </label>
                </div>
              </div>
              <div className="mt-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded p-3" data-id="element-4516">
                <p className="text-sm text-blue-800 dark:text-blue-300 flex items-start" data-id="element-4517">
                  <HelpCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-4518" />
                  <span data-id="element-4519">
                    <strong data-id="element-4520">Note:</strong> Settlement will always be in the
                    merchant's settlement currency. Exchange rates will be
                    applied for transactions in other currencies.
                  </span>
                </p>
              </div>
              <div className="mt-6" data-id="element-4521">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4522">
                  Exchange Rate Markup (%)
                </label>
                <div className="flex items-center" data-id="element-4523">
                  <input type="number" className="w-24 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="2.5" defaultValue="2.5" step="0.1" min="0" data-id="element-4524" />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-4525">
                    % added to the base exchange rate
                  </span>
                </div>
              </div>
            </div>
          </Card>}
        {/* Document Uploads */}
        {activeTab === 'document-uploads' && <Card data-id="element-4526">
            <div className="space-y-6" data-id="element-4527">
              <div data-id="element-4528">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4529">
                  CNIC Document
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.cnic.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-4530">
                  <div className="space-y-2 text-center" data-id="element-4531">
                    {merchant.documents.cnic.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-4532" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-4533">
                          <p className="font-medium" data-id="element-4534">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-4535">{merchant.documents.cnic.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-4536">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4537" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-4538">
                          <label htmlFor="cnic-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-4539">
                            <span data-id="element-4540">Upload a file</span>
                            <input id="cnic-upload" name="cnic-upload" type="file" className="sr-only" onChange={() => handleFileUpload('cnic')} data-id="element-4541" />
                          </label>
                          <p className="pl-1" data-id="element-4542">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4543">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
              <div data-id="element-4544">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4545">
                  Proof of Address
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.proofOfAddress.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-4546">
                  <div className="space-y-2 text-center" data-id="element-4547">
                    {merchant.documents.proofOfAddress.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-4548" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-4549">
                          <p className="font-medium" data-id="element-4550">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-4551">{merchant.documents.proofOfAddress.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-4552">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4553" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-4554">
                          <label htmlFor="address-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-4555">
                            <span data-id="element-4556">Upload a file</span>
                            <input id="address-upload" name="address-upload" type="file" className="sr-only" onChange={() => handleFileUpload('proofOfAddress')} data-id="element-4557" />
                          </label>
                          <p className="pl-1" data-id="element-4558">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4559">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
              <div data-id="element-4560">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4561">
                  Business Registration
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.businessRegistration.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-4562">
                  <div className="space-y-2 text-center" data-id="element-4563">
                    {merchant.documents.businessRegistration.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-4564" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-4565">
                          <p className="font-medium" data-id="element-4566">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-4567">{merchant.documents.businessRegistration.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-4568">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4569" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-4570">
                          <label htmlFor="registration-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-4571">
                            <span data-id="element-4572">Upload a file</span>
                            <input id="registration-upload" name="registration-upload" type="file" className="sr-only" onChange={() => handleFileUpload('businessRegistration')} data-id="element-4573" />
                          </label>
                          <p className="pl-1" data-id="element-4574">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4575">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
              <div data-id="element-4576">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4577">
                  Bank Letter
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.bankLetter.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-4578">
                  <div className="space-y-2 text-center" data-id="element-4579">
                    {merchant.documents.bankLetter.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-4580" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-4581">
                          <p className="font-medium" data-id="element-4582">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-4583">{merchant.documents.bankLetter.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-4584">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4585" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-4586">
                          <label htmlFor="bank-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-4587">
                            <span data-id="element-4588">Upload a file</span>
                            <input id="bank-upload" name="bank-upload" type="file" className="sr-only" onChange={() => handleFileUpload('bankLetter')} data-id="element-4589" />
                          </label>
                          <p className="pl-1" data-id="element-4590">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4591">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
            </div>
          </Card>}
        {/* Compliance Screening */}
        {activeTab === 'compliance-screening' && <Card data-id="element-4592">
            <div className="space-y-6" data-id="element-4593">
              <div className={`p-4 rounded-md ${merchant.complianceStatus === 'passed' ? 'bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800' : merchant.complianceStatus === 'failed' ? 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800' : 'bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800'}`} data-id="element-4594">
                <div className="flex" data-id="element-4595">
                  <div className="flex-shrink-0" data-id="element-4596">
                    {merchant.complianceStatus === 'passed' ? <CheckIcon className="h-5 w-5 text-green-400" data-id="element-4597" /> : merchant.complianceStatus === 'failed' ? <XIcon className="h-5 w-5 text-red-400" data-id="element-4598" /> : <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-4599" />}
                  </div>
                  <div className="ml-3" data-id="element-4600">
                    <h3 className={`text-sm font-medium ${merchant.complianceStatus === 'passed' ? 'text-green-800 dark:text-green-300' : merchant.complianceStatus === 'failed' ? 'text-red-800 dark:text-red-300' : 'text-yellow-800 dark:text-yellow-300'}`} data-id="element-4601">
                      AML Screening:{' '}
                      {merchant.complianceStatus.charAt(0).toUpperCase() + merchant.complianceStatus.slice(1)}
                    </h3>
                    <div className={`mt-2 text-sm ${merchant.complianceStatus === 'passed' ? 'text-green-700 dark:text-green-400' : merchant.complianceStatus === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-yellow-700 dark:text-yellow-400'}`} data-id="element-4602">
                      <p data-id="element-4603">
                        {merchant.complianceStatus === 'passed' ? 'All compliance checks have passed successfully.' : merchant.complianceStatus === 'failed' ? 'Compliance check failed. See details below.' : 'Compliance check is pending. Submit to initiate the check.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-4604">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4605">
                  Compliance Review Comments
                </label>
                <textarea name="complianceNotes" value={merchant.complianceNotes} onChange={handleInputChange} rows={4} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter any notes or comments related to compliance review" data-id="element-4606" />
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-4607">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4608">
                  Blacklist Check
                </h3>
                <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-4609">
                  <div className="flex items-center" data-id="element-4610">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-4611"></span>
                    <span className="text-sm" data-id="element-4612">OFAC Sanctions List</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-4613">
                    Passed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-4614">
                  <div className="flex items-center" data-id="element-4615">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-4616"></span>
                    <span className="text-sm" data-id="element-4617">PEP Check</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-4618">
                    Passed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-4619">
                  <div className="flex items-center" data-id="element-4620">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-4621"></span>
                    <span className="text-sm" data-id="element-4622">Adverse Media</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-4623">
                    Passed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2" data-id="element-4624">
                  <div className="flex items-center" data-id="element-4625">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-4626"></span>
                    <span className="text-sm" data-id="element-4627">Local Blacklist</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-4628">
                    Passed
                  </span>
                </div>
              </div>
              <div className="flex justify-end" data-id="element-4629">
                <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4630">
                  Re-run Compliance Check
                </button>
              </div>
            </div>
          </Card>}
        {/* Location Tagging */}
        {activeTab === 'location-tagging' && <Card data-id="element-4631">
            <div className="space-y-4" data-id="element-4632">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4633">
                Mark the geo-coordinates of the merchant's physical location on
                the map:
              </p>
              <div className="h-96 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center" data-id="element-4634">
                <div className="text-center" data-id="element-4635">
                  <MapPinIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" data-id="element-4636" />
                  <p className="mt-2 text-gray-500 dark:text-gray-400" data-id="element-4637">
                    Interactive map would be displayed here
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4638">
                <div data-id="element-4639">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4640">
                    Latitude
                  </label>
                  <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="e.g. 40.7128" value={merchant.location.lat || ''} readOnly data-id="element-4641" />
                </div>
                <div data-id="element-4642">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4643">
                    Longitude
                  </label>
                  <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="e.g. -74.0060" value={merchant.location.lng || ''} readOnly data-id="element-4644" />
                </div>
              </div>
              <div className="flex space-x-3" data-id="element-4645">
                <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4646">
                  Use Current Location
                </button>
                <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4647">
                  Enter Coordinates Manually
                </button>
              </div>
              <div className="mt-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded p-3" data-id="element-4648">
                <p className="text-sm text-blue-800 dark:text-blue-300 flex items-center" data-id="element-4649">
                  <HelpCircleIcon className="h-4 w-4 mr-2" data-id="element-4650" />
                  Location tagging helps with fraud prevention and enables
                  location-based services.
                </p>
              </div>
            </div>
          </Card>}
      </form>
      {/* Form Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-4651">
        <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/merchants')} data-id="element-4652">
          Cancel
        </button>
        <div className="flex space-x-3" data-id="element-4653">
          <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4654">
            Save as Draft
          </button>
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-4655">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-4656" />
            {isEditing ? 'Update' : 'Submit'} Merchant
          </button>
        </div>
      </div>
      {/* Change Status Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title="Change Merchant Status" footer={<>
            <button onClick={() => setShowStatusModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4658">
              Cancel
            </button>
            <button onClick={confirmStatusChange} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={!newStatus || newStatus === merchant.status} data-id="element-4659">
              Update Status
            </button>
          </>} data-id="element-4657">
        <div className="space-y-4" data-id="element-4660">
          <div className="flex items-center mb-4" data-id="element-4661">
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-4662">
              <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-4663" />
            </div>
            <div data-id="element-4664">
              <h3 className="text-lg font-medium" data-id="element-4665">{merchant.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4666">
                Current Status: {getStatusBadge(merchant.status)}
              </p>
            </div>
          </div>
          <div data-id="element-4667">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-4668">
              New Status
            </label>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-4669">
              <option value="" data-id="element-4670">Select Status</option>
              <option value="active" data-id="element-4671">Active</option>
              <option value="inactive" data-id="element-4672">Inactive</option>
              <option value="pending" data-id="element-4673">Pending</option>
              <option value="suspended" data-id="element-4674">Suspended</option>
            </select>
          </div>
          <div data-id="element-4675">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-4676">
              Reason for Status Change
            </label>
            <textarea value={statusChangeReason} onChange={e => setStatusChangeReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter reason for status change" required data-id="element-4677" />
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-4678">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-start" data-id="element-4679">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-4680" />
              <span data-id="element-4681">
                <strong data-id="element-4682">Note:</strong> Changing a merchant's status may affect
                their ability to process transactions. This action will be
                recorded in the audit log.
              </span>
            </p>
          </div>
        </div>
      </Modal>
      {/* Status History Modal */}
      <Modal isOpen={showStatusHistoryModal} onClose={() => setShowStatusHistoryModal(false)} title="Status Change History" size="lg" data-id="element-4683">
        <div className="space-y-4" data-id="element-4684">
          <div className="flex items-center mb-4" data-id="element-4685">
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-4686">
              <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-4687" />
            </div>
            <div data-id="element-4688">
              <h3 className="text-lg font-medium" data-id="element-4689">{merchant.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4690">
                Current Status: {getStatusBadge(merchant.status)}
              </p>
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4" data-id="element-4691">
            <h4 className="font-medium text-gray-700 dark:text-gray-300" data-id="element-4692">
              Complete Status History
            </h4>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto" data-id="element-4693">
            {merchant.statusHistory.map((history, index) => <div key={index} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md" data-id="element-4694">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center" data-id="element-4695">
                  <div className="flex items-center mb-2 sm:mb-0" data-id="element-4696">
                    {history.previousStatus ? <div className="flex items-center" data-id="element-4697">
                        <span className="inline-block" data-id="element-4698">
                          {getStatusBadge(history.previousStatus)}
                        </span>
                        <ArrowRightIcon className="h-4 w-4 mx-2 text-gray-500 dark:text-gray-400" data-id="element-4699" />
                        <span className="inline-block" data-id="element-4700">
                          {getStatusBadge(history.newStatus)}
                        </span>
                      </div> : <div className="flex items-center" data-id="element-4701">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mr-2" data-id="element-4702">
                          Initial status:
                        </span>
                        <span className="inline-block" data-id="element-4703">
                          {getStatusBadge(history.newStatus)}
                        </span>
                      </div>}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4704">
                    {formatDateTime(history.changedAt)}
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-4705">
                  <span className="font-medium" data-id="element-4706">Reason:</span> {history.reason}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4707">
                  Changed by: {history.changedBy}
                </div>
              </div>)}
            {merchant.statusHistory.length === 0 && <div className="text-center py-4 text-gray-500 dark:text-gray-400" data-id="element-4708">
                No status change history available.
              </div>}
          </div>
        </div>
      </Modal>
    </div>;
};
export default MerchantForm;